import { Marble } from './marble';
import { initialZoom, Skills, zoomThreshold } from './data/constants';
import { ParticleManager } from './particleManager';
import { StageDef, stages } from './data/maps';
import { parseName } from './utils/utils';
import { Camera } from './camera';
import { RouletteRenderer } from './rouletteRenderer';
import { SkillEffect } from './skillEffect';
import { GameObject } from './gameObject';
import options from './options';
import { bound } from './utils/bound.decorator';
import { UIObject } from './UIObject';
import { RankRenderer } from './rankRenderer';
import { Minimap } from './minimap';
import { TopCandidatesRenderer } from './topCandidatesRenderer';
import { IPhysics } from './IPhysics';
import { Box2dPhysics } from './physics-box2d';
import { MouseEventHandlerName, MouseEventName } from './types/mouseEvents.type';
import { FastForwader } from './fastForwader';

export class Roulette extends EventTarget {
  private _marbles: Marble[] = [];

  private _lastTime: number = 0;
  private _elapsed: number = 0;
  private _noMoveDuration: number = 0;
  private _shakeAvailable: boolean = false;

  private _updateInterval = 10;
  private _timeScale = 1;
  private _speed = 1;

  private _winners: Marble[] = [];
  private _particleManager = new ParticleManager();
  private _stage: StageDef | null = null;

  private _camera: Camera = new Camera();
  private _renderer: RouletteRenderer = new RouletteRenderer();

  private _effects: GameObject[] = [];

  private _winnerRank = 0;
  private _totalMarbleCount = 0;
  private _goalDist: number = Infinity;
  private _isRunning: boolean = false;
  private _winner: Marble | null = null;

  private _uiObjects: UIObject[] = [];

  private physics!: IPhysics;

  private _isReady: boolean = false;
  private fastForwarder!: FastForwader;
  private topCandidatesRenderer!: TopCandidatesRenderer;

  get isReady() {
    return this._isReady;
  }

  constructor() {
    super();
    this._renderer.init().then(() => {
      this._init().then(() => {
        this._isReady = true;
        this._update();
      });
    });
  }

  public getZoom() {
    return initialZoom * this._camera.zoom;
  }

  private addUiObject(obj: UIObject) {
    this._uiObjects.push(obj);
    if (obj.onWheel) {
      this._renderer.canvas.addEventListener('wheel', obj.onWheel);
    }
    if (obj.onMessage) {
      obj.onMessage((msg) => {
        console.log('onMessage', msg);
        this.dispatchEvent(new CustomEvent('message', { detail: msg }));
      });
    }
  }

  @bound
  private _update() {
    if (!this._lastTime) this._lastTime = Date.now();
    const currentTime = Date.now();

    this._elapsed += (currentTime - this._lastTime) * this._speed * this.fastForwarder.speed;
    if (this._elapsed > 100) {
      this._elapsed %= 100;
    }
    this._lastTime = currentTime;

    const interval = (this._updateInterval / 1000) * this._timeScale;

    while (this._elapsed >= this._updateInterval) {
      this.physics.step(interval);
      this._updateMarbles(this._updateInterval);
      this._particleManager.update(this._updateInterval);
      this._updateEffects(this._updateInterval);
      this._elapsed -= this._updateInterval;
      this._uiObjects.forEach((obj) => obj.update(this._updateInterval));
    }

    if (this._marbles.length > 1) {
      this._marbles.sort((a, b) => b.y - a.y);
    }

    if (this._stage) {
      this._camera.update({
        marbles: this._marbles,
        stage: this._stage,
        needToZoom: this._goalDist < (zoomThreshold * 4),
        targetIndex:
          this._winners.length > 0
            ? this._winnerRank - this._winners.length
            : 0,
        winnerRank: this._winnerRank,
        winnersCount: this._winners.length,
        totalMarbleCount: this._totalMarbleCount,
      });

      if (
        this._isRunning &&
        this._marbles.length > 0 &&
        this._noMoveDuration > 3000
      ) {
        this._changeShakeAvailable(true);
      } else {
        this._changeShakeAvailable(false);
      }
    }

    this._render();
    window.requestAnimationFrame(this._update);
  }

  private _updateMarbles(deltaTime: number) {
    if (!this._stage) return;

    const winnerCount = options.winnerCount;
    const isLastWinner = this._winnerRank >= this._totalMarbleCount - 1;

    for (let i = 0; i < this._marbles.length; i++) {
      const marble = this._marbles[i];
      marble.update(deltaTime);
      if (marble.skill === Skills.Impact) {
        this._effects.push(new SkillEffect(marble.x, marble.y));
        this.physics.impact(marble.id);
      }
      if (marble.y > this._stage.goalY) {
        this._winners.push(marble);
        
        // 우승자 수에 따른 게임 종료 로직 분기
        if (winnerCount === 1) {
          // 1명 우승자: 기존 로직 (폭죽 + 우승자 표시 + topCandidates 숨김)
          if (this._isRunning && this._winners.length === this._winnerRank + 1) {
            this.dispatchEvent(
              new CustomEvent('goal', { detail: { winner: marble.name } }),
            );
            this._winner = marble;
            this._isRunning = false;
            this._particleManager.shot(
              this._renderer.width,
              this._renderer.height,
            );
            this.topCandidatesRenderer.setGameStarted(false);
          } else if (
            this._isRunning &&
            this._winnerRank === this._winners.length &&
            this._winnerRank === this._totalMarbleCount - 1
          ) {
            this.dispatchEvent(
              new CustomEvent('goal', {
                detail: { winner: this._marbles[i + 1].name },
              }),
            );
            this._winner = this._marbles[i + 1];
            this._isRunning = false;
            this._particleManager.shot(
              this._renderer.width,
              this._renderer.height,
            );
            this.topCandidatesRenderer.setGameStarted(false);
          }
        } else {
          // 2명 이상 우승자: topCandidates 유지
          if (isLastWinner) {
            // 마지막 당첨 모드: 골인 후 남은 구슬이 winnerCount 명일 때 게임 종료
            // for 루프 내에서 아직 marbles가 필터링되지 않았으므로 marbles.length - 1이 실제 남은 수
            const remainingAfterGoal = this._marbles.length - 1;
            if (this._isRunning && remainingAfterGoal === winnerCount) {
              this.dispatchEvent(
                new CustomEvent('goal', { detail: { winner: `최종 ${winnerCount}인 확정!` } }),
              );
              this._isRunning = false;
              this._particleManager.shot(
                this._renderer.width,
                this._renderer.height,
              );
              // topCandidates는 유지
            }
          } else {
            // 첫번째 당첨 모드: 골인한 구슬이 winnerCount 명일 때 게임 종료
            if (this._isRunning && this._winners.length === winnerCount) {
              this.dispatchEvent(
                new CustomEvent('goal', { detail: { winner: `최종 ${winnerCount}인 확정!` } }),
              );
              this._isRunning = false;
              this._particleManager.shot(
                this._renderer.width,
                this._renderer.height,
              );
              // topCandidates는 유지
            }
          }
        }
        
        setTimeout(() => {
          this.physics.removeMarble(marble.id);
        }, 500);
      }
    }

    const targetIndex = this._winnerRank - this._winners.length;
    const topY = this._marbles[targetIndex] ? this._marbles[targetIndex].y : 0;
    this._goalDist = Math.abs(this._stage.zoomY - topY);
    this._timeScale = this._calcTimeScale();

    this._marbles = this._marbles.filter(
      (marble) => marble.y <= this._stage!.goalY,
    );
  }

  private _calcTimeScale(): number {
    if (!this._stage) return 1;
    const targetIndex = this._winnerRank - this._winners.length;
    
    // 마지막 당첨 모드인지 확인
    const isLastWinner = this._winnerRank >= this._totalMarbleCount - 1;
    const hasWinners = this._winners.length > 0;
    
    // 마지막 당첨 모드이고, 첫 번째 구슬이 통과했으며, 구슬이 10개 이하일 때 슬로우모션
    if (isLastWinner && hasWinners && this._marbles.length <= 10) {
      return 0.3; // 슬로우모션 (최종 5인 결정 전까지)
    }
    
    // 첫번째 당첨 모드이고, 결승지점 근처일 때 슬로우모션
    if (!isLastWinner && this._goalDist < zoomThreshold * 3) {
      return 0.3; // 슬로우모션
    }
    
    if (
      this._winners.length < this._winnerRank + 1 &&
      this._goalDist < zoomThreshold
    ) {
      if (
        this._marbles[targetIndex].y >
        this._stage.zoomY - zoomThreshold * 1.0 &&
        (this._marbles[targetIndex - 1] || this._marbles[targetIndex + 1])
      ) {
        return Math.max(0.2, this._goalDist / zoomThreshold);
      }
    }
    return 1;
  }

  private _updateEffects(deltaTime: number) {
    this._effects.forEach((effect) => effect.update(deltaTime));
    this._effects = this._effects.filter((effect) => !effect.isDestroy);
  }

  private _render() {
    if (!this._stage) return;
    const renderParams = {
      camera: this._camera,
      stage: this._stage,
      entities: this.physics.getEntities(),
      marbles: this._marbles,
      winners: this._winners,
      particleManager: this._particleManager,
      effects: this._effects,
      winnerRank: this._winnerRank,
      winner: this._winner,
      size: { x: this._renderer.width, y: this._renderer.height },
      totalMarbleCount: this._totalMarbleCount,
      winnerCount: options.winnerCount,
    };
    this._renderer.render(renderParams, this._uiObjects);
  }

  private async _init() {
    this.physics = new Box2dPhysics();
    await this.physics.init();

    this.addUiObject(new RankRenderer());
    this.attachEvent();
    const minimap = new Minimap();
    minimap.onViewportChange((pos) => {
      if (pos) {
        this._camera.setPosition(pos, false);
        this._camera.lock(true);
      } else {
        this._camera.lock(false);
      }
    });
    this.addUiObject(minimap);
    this.fastForwarder = new FastForwader();
    this.addUiObject(this.fastForwarder);
    this.topCandidatesRenderer = new TopCandidatesRenderer();
    this.addUiObject(this.topCandidatesRenderer);
    this._stage = stages[0];
    this._loadMap();
  }

  @bound
  private mouseHandler(eventName: MouseEventName, e: MouseEvent) {
    const handlerName = `on${eventName}` as MouseEventHandlerName;

    const sizeFactor = this._renderer.sizeFactor;
    const pos = { x: e.offsetX * sizeFactor, y: e.offsetY * sizeFactor };
    this._uiObjects.forEach((obj) => {
      if (!obj[handlerName]) return;
      const bounds = obj.getBoundingBox();
      if (!bounds) {
        obj[handlerName]({ ...pos, button: e.button });
      } else if (
        bounds &&
        pos.x >= bounds.x &&
        pos.y >= bounds.y &&
        pos.x <= bounds.x + bounds.w &&
        pos.y <= bounds.y + bounds.h
      ) {
        obj[handlerName]({ x: pos.x - bounds.x, y: pos.y - bounds.y, button: e.button });
      } else {
        obj[handlerName](undefined);
      }
    });
  }

  private attachEvent() {
    ['MouseMove', 'MouseUp', 'MouseDown', 'DblClick'].forEach(
      (ev) => {
        // @ts-ignore
        this._renderer.canvas.addEventListener(ev.toLowerCase().replace('mouse', 'pointer'), this.mouseHandler.bind(this, ev));
      },
    );
    this._renderer.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }

  private _loadMap() {
    if (!this._stage) {
      throw new Error('No map has been selected');
    }

    this.physics.createStage(this._stage);
    this._camera.initializePosition();
  }

  public clearMarbles() {
    this.physics.clearMarbles();
    this._winner = null;
    this._winners = [];
    this._marbles = [];
  }

  public start() {
    this._isRunning = true;
    this._winnerRank = options.winningRank;
    if (this._winnerRank >= this._marbles.length) {
      this._winnerRank = this._marbles.length - 1;
    }
    this._camera.startFollowingMarbles();
    
    this.physics.start();
    this._marbles.forEach((marble) => (marble.isActive = true));
    
    // 게임 시작 시 상위 후보 표시 활성화
    this.topCandidatesRenderer.setGameStarted(true);
  }

  public setSpeed(value: number) {
    if (value <= 0) {
      throw new Error('Speed multiplier must larger than 0');
    }
    this._speed = value;
  }

  public getSpeed() {
    return this._speed;
  }

  public setWinningRank(rank: number) {
    this._winnerRank = rank;
  }

  public setTitle(title: string) {
    this._renderer.setTitle(title);
  }

  public setMarbles(names: string[]) {
    this.reset();
    const arr = names.slice();

    let maxWeight = -Infinity;
    let minWeight = Infinity;

    const members = arr
      .map((nameString) => {
        const result = parseName(nameString);
        if (!result) return null;
        const { name, weight, count } = result;
        if (weight > maxWeight) maxWeight = weight;
        if (weight < minWeight) minWeight = weight;
        return { name, weight, count };
      })
      .filter((member) => !!member);

    const gap = maxWeight - minWeight;

    let totalCount = 0;
    members.forEach((member) => {
      if (member) {
        member.weight = 0.1 + (gap ? (member.weight - minWeight) / gap : 0);
        totalCount += member.count;
      }
    });

    const orders = Array(totalCount)
      .fill(0)
      .map((_, i) => i)
      .sort(() => Math.random() - 0.5);
    members.forEach((member) => {
      if (member) {
        for (let j = 0; j < member.count; j++) {
          const order = orders.pop() || 0;
          this._marbles.push(
            new Marble(
              this.physics,
              order,
              totalCount,
              member.name,
              member.weight,
            ),
          );
        }
      }
    });
    this._totalMarbleCount = totalCount;
  }

  private _clearMap() {
    this.physics.clear();
    this._marbles = [];
  }

  public reset() {
    this.clearMarbles();
    this._clearMap();
    this._loadMap();
    this._goalDist = Infinity;
  }

  public getCount() {
    return this._marbles.length;
  }

  private _changeShakeAvailable(v: boolean) {
    if (this._shakeAvailable !== v) {
      this._shakeAvailable = v;
      this.dispatchEvent(
        new CustomEvent('shakeAvailableChanged', { detail: v }),
      );
    }
  }

  public shake() {
    if (!this._shakeAvailable) return;
  }

  public getMaps() {
    return stages.map((stage, index) => {
      return {
        index,
        title: stage.title,
      };
    });
  }

  public setMap(index: number) {
    if (index < 0 || index > stages.length - 1) {
      throw new Error('Incorrect map number');
    }
    const names = this._marbles.map((marble) => marble.name);
    this._stage = stages[index];
    this.setMarbles(names);
    this._camera.initializePosition();
  }
}